import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type {
  TimeSpeed,
  TimeDilator,
  AttentionEntry,
  MobIntent,
  HpTier,
  ConditionType,
} from '@machina/core';

// =============================================================================
// TYPES
// =============================================================================

export type AISuggestionSource = 'MobDirector' | 'StoryDirector' | 'Narrator';
export type AISuggestionStatus = 'pending' | 'approved' | 'rejected' | 'edited';

export interface AISuggestion {
  id: string;
  source: AISuggestionSource;
  summary: string;
  detail?: string;
  timestamp: Date;
  status: AISuggestionStatus;
  mobId?: string;
  originalIntent?: MobIntent;
  editedIntent?: MobIntent;
}

export interface MobDisplay {
  id: string;
  name: string;
  hpTier: HpTier;
  hpPercent: number;
  conditions: ConditionType[];
  intent?: MobIntent;
  attentionEntries: AttentionEntry[];
}

// DM notes per character (campaign-relevant info)
export interface CharacterDMNotes {
  goals: string;
  sidequests: string;
  openItems: string;
}

export interface PanelSection {
  id: string;
  label: string;
  collapsed: boolean;
}

// =============================================================================
// STORE
// =============================================================================

interface DMConsoleState {
  // Panel state
  isOpen: boolean;
  sections: PanelSection[];

  // Time dilator
  timeDilator: TimeDilator;

  // AI Suggestions queue
  suggestions: AISuggestion[];
  selectedSuggestionIndex: number;
  autoApproveNarration: boolean;

  // Attention table
  attentionEntries: AttentionEntry[];

  // Mob intents
  mobs: MobDisplay[];

  // DM notes per character
  dmNotes: Record<string, CharacterDMNotes>;

  // Actions - Panel
  togglePanel: () => void;
  toggleSection: (sectionId: string) => void;

  // Actions - Time Dilator
  setTimeSpeed: (speed: TimeSpeed) => void;
  cycleTimeSpeed: (direction: 'faster' | 'slower') => void;
  togglePause: () => void;

  // Actions - Suggestions
  addSuggestion: (suggestion: Omit<AISuggestion, 'id' | 'timestamp' | 'status'>) => void;
  approveSuggestion: (id: string) => void;
  rejectSuggestion: (id: string) => void;
  editSuggestion: (id: string, editedIntent: MobIntent) => void;
  selectNextSuggestion: () => void;
  selectPrevSuggestion: () => void;
  clearApproved: () => void;

  // Actions - Attention
  setAttentionEntries: (entries: AttentionEntry[]) => void;

  // Actions - Mobs
  setMobs: (mobs: MobDisplay[]) => void;
  updateMobIntent: (mobId: string, intent: MobIntent) => void;

  // Actions - DM Notes
  getDMNotes: (characterId: string) => CharacterDMNotes;
  setDMNotes: (characterId: string, notes: Partial<CharacterDMNotes>) => void;
}

const TIME_SPEED_ORDER: TimeSpeed[] = ['PAUSE', 'SLOW', 'NORMAL', 'FAST', 'MAX'];

const DEFAULT_SECTIONS: PanelSection[] = [
  { id: 'party', label: 'Party', collapsed: false },
  { id: 'tokens', label: 'Token Cache', collapsed: true },
  { id: 'room', label: 'Room Setup', collapsed: false },
  { id: 'map', label: 'Battle Map', collapsed: false },
  { id: 'lighting', label: 'Lighting & Vision', collapsed: false },
  { id: 'factions', label: 'Factions & Reputation', collapsed: true },
  { id: 'psyche', label: 'Psyche Voices', collapsed: true },
  { id: 'time', label: 'Sands of Time', collapsed: true },
  { id: 'battlesim', label: 'Battle Sim', collapsed: true },
  { id: 'queue', label: 'The Deep', collapsed: true },
  { id: 'attention', label: 'Attention', collapsed: true },
  { id: 'intents', label: 'Monster Intents', collapsed: true },
];

export const useDMConsoleStore = create<DMConsoleState>()(
  persist(
    (set, get) => ({
      // Initial state
      isOpen: true,
      sections: DEFAULT_SECTIONS,

      timeDilator: {
        speed: 'NORMAL',
        worldTickBaseMs: 10000,
        combatRoundBaseMs: 6000,
        pausedAt: null as Date | null,
        skipToTime: null as Date | null,
      } as TimeDilator,

      suggestions: [],
      selectedSuggestionIndex: 0,
      autoApproveNarration: false,

      attentionEntries: [],
      mobs: [],
      dmNotes: {},

      // Panel actions
      togglePanel: () => set((state) => ({ isOpen: !state.isOpen })),

      toggleSection: (sectionId) =>
        set((state) => ({
          sections: state.sections.map((s) =>
            s.id === sectionId ? { ...s, collapsed: !s.collapsed } : s
          ),
        })),

      // Time dilator actions
      setTimeSpeed: (speed) =>
        set((state) => ({
          timeDilator: {
            ...state.timeDilator,
            speed,
            pausedAt: speed === 'PAUSE' ? new Date() : null,
          },
        })),

      cycleTimeSpeed: (direction) =>
        set((state) => {
          const currentIndex = TIME_SPEED_ORDER.indexOf(state.timeDilator.speed);
          let newIndex: number;

          if (direction === 'faster') {
            newIndex = Math.min(currentIndex + 1, TIME_SPEED_ORDER.length - 1);
          } else {
            newIndex = Math.max(currentIndex - 1, 0);
          }

          const newSpeed = TIME_SPEED_ORDER[newIndex];
          return {
            timeDilator: {
              ...state.timeDilator,
              speed: newSpeed,
              pausedAt: newSpeed === 'PAUSE' ? new Date() : null,
            },
          };
        }),

      togglePause: () =>
        set((state) => {
          const isPaused = state.timeDilator.speed === 'PAUSE';
          return {
            timeDilator: {
              ...state.timeDilator,
              speed: isPaused ? 'NORMAL' : 'PAUSE',
              pausedAt: isPaused ? null : new Date(),
            },
          };
        }),

      // Suggestion actions
      addSuggestion: (suggestion) =>
        set((state) => ({
          suggestions: [
            {
              ...suggestion,
              id: crypto.randomUUID(),
              timestamp: new Date(),
              status: 'pending',
            },
            ...state.suggestions,
          ],
        })),

      approveSuggestion: (id) =>
        set((state) => ({
          suggestions: state.suggestions.map((s) =>
            s.id === id ? { ...s, status: 'approved' as const } : s
          ),
        })),

      rejectSuggestion: (id) =>
        set((state) => ({
          suggestions: state.suggestions.map((s) =>
            s.id === id ? { ...s, status: 'rejected' as const } : s
          ),
        })),

      editSuggestion: (id, editedIntent) =>
        set((state) => ({
          suggestions: state.suggestions.map((s) =>
            s.id === id ? { ...s, status: 'edited' as const, editedIntent } : s
          ),
        })),

      selectNextSuggestion: () =>
        set((state) => {
          const pendingCount = state.suggestions.filter((s) => s.status === 'pending').length;
          return {
            selectedSuggestionIndex: Math.min(
              state.selectedSuggestionIndex + 1,
              pendingCount - 1
            ),
          };
        }),

      selectPrevSuggestion: () =>
        set((state) => ({
          selectedSuggestionIndex: Math.max(state.selectedSuggestionIndex - 1, 0),
        })),

      clearApproved: () =>
        set((state) => ({
          suggestions: state.suggestions.filter(
            (s) => s.status === 'pending'
          ),
          selectedSuggestionIndex: 0,
        })),

      // Attention actions
      setAttentionEntries: (entries) => set({ attentionEntries: entries }),

      // Mob actions
      setMobs: (mobs) => set({ mobs }),

      updateMobIntent: (mobId, intent) =>
        set((state) => ({
          mobs: state.mobs.map((m) =>
            m.id === mobId ? { ...m, intent } : m
          ),
        })),

      // DM Notes actions
      getDMNotes: (characterId: string): CharacterDMNotes => {
        const state = get();
        return state.dmNotes[characterId] || { goals: '', sidequests: '', openItems: '' };
      },

      setDMNotes: (characterId: string, notes: Partial<CharacterDMNotes>) =>
        set((state) => ({
          dmNotes: {
            ...state.dmNotes,
            [characterId]: {
              ...(state.dmNotes[characterId] || { goals: '', sidequests: '', openItems: '' }),
              ...notes,
            },
          },
        })),
    }),
    {
      name: 'dm-console-storage',
      version: 4, // Bump version to add factions + psyche sections
      partialize: (state) => ({
        isOpen: state.isOpen,
        sections: state.sections,
        autoApproveNarration: state.autoApproveNarration,
        dmNotes: state.dmNotes,
      }),
      // Merge stored sections with defaults to add any new sections
      merge: (persistedState, currentState) => {
        const persisted = persistedState as Partial<DMConsoleState>;
        const storedSections = persisted.sections || [];
        const storedIds = new Set(storedSections.map((s) => s.id));

        // Add any sections from defaults that aren't in stored state
        const mergedSections = [
          ...storedSections,
          ...DEFAULT_SECTIONS.filter((s) => !storedIds.has(s.id)),
        ];

        // Sort to match DEFAULT_SECTIONS order
        const orderMap = new Map(DEFAULT_SECTIONS.map((s, i) => [s.id, i]));
        mergedSections.sort((a, b) => (orderMap.get(a.id) ?? 99) - (orderMap.get(b.id) ?? 99));

        return {
          ...currentState,
          ...persisted,
          sections: mergedSections,
        };
      },
      // Migration from old versions
      migrate: (persistedState, version) => {
        if (version < 4) {
          // Version 1/2/3 -> 4: ensure all sections exist (including factions + psyche)
          const state = persistedState as Partial<DMConsoleState>;
          const storedSections = state.sections || [];
          const storedIds = new Set(storedSections.map((s) => s.id));

          const mergedSections = [
            ...storedSections,
            ...DEFAULT_SECTIONS.filter((s) => !storedIds.has(s.id)),
          ];

          const orderMap = new Map(DEFAULT_SECTIONS.map((s, i) => [s.id, i]));
          mergedSections.sort((a, b) => (orderMap.get(a.id) ?? 99) - (orderMap.get(b.id) ?? 99));

          return { ...state, sections: mergedSections };
        }
        return persistedState;
      },
    }
  )
);
